import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import SolidAngleRightIcon from 'LuxKit/Icons/solid/SolidAngleRightIcon'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import type { ReactElement } from 'react'
import { useCallback } from 'react'
import styled from 'styled-components'
import focusStyle from 'styles/utils/focusStyle'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import Group from 'components/utils/Group'

const IconContainer = styled.div`
  grid-area: icon; 
  opacity: 0;
  justify-self: end;

  transition: opacity 200ms;
`
const Container = styled(Clickable)`
  box-shadow: inset 0 0 0 ${rem(1)} ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.L};
  display: grid;
  gap: ${rem(16)};
  padding: ${rem(12)} ${rem(20)} ${rem(12)};
  grid-template: "symbol content icon" auto / auto 1fr auto;
  align-items: center;
  transition: box-shadow 200ms;
  background: ${props => props.theme.palette.neutral.default.seven};


&.withOutBackground {
  background: transparent;
}

  &.selected {
    box-shadow: inset 0 0 0 ${rem(2)} ${props => props.theme.palette.neutral.default.one};
    > ${IconContainer} {
      opacity: 1;
    }
  }

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)};
    padding: ${rem(20)} ${rem(16)};
    grid-template:
      "symbol icon" auto
      "content content" auto / 1fr auto;

    > ${IconContainer} {
      align-self: self-start;
    }

    &.layout-horizontal {
      gap: ${rem(16)};
      padding: ${rem(12)} ${rem(20)} ${rem(12)};
      grid-template: "symbol content icon" auto / auto 1fr auto;

      > ${IconContainer} {
        align-self: center;
      }
    }
  }

  ${mediaHoverable} {
    &:hover {
      box-shadow: inset 0 0 0 ${rem(2)} ${props => props.theme.palette.neutral.default.one};
      > ${IconContainer} {
        opacity: 1;
      }
    }
  }

  &:focus-visible {
    ${focusStyle}
  }
`

const SymbolContainer = styled.div`
  grid-area: symbol;
  /* Handle both emoji + svg icon */
  font-size: ${rem(32)};
  ${setSvgIconSize(32)}
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  ${mediaQueryUp.desktop} {
    font-size: ${rem(40)};
    ${setSvgIconSize(40)}
  }
`

const ContentContainer = styled(VerticalSpacer)`
  grid-area: content;
`

const StatusLabelMobile = styled(StatusLabel)`
  flex-shrink: 0;
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const StatusLabelDesktop = styled(StatusLabel)`
  display: none;
  
  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

interface Props {
  title: string
  subtitle?: string
  /**
   * @description This field accept both SVG Icon and Emoji
   */
  icon?: ReactElement | string
  // imageUrl?: string
  /**
   * @default auto
   */
  layout?: 'auto' | 'horizontal'
  interactionIcon?: 'right' | 'check'
  selected?: boolean
  value: string
  disabled?: boolean
  onClick: (value: string) => void
  withOutBackground?: boolean
  statusLabel?: {
    text: string
    variant: 'confirmation' | 'cancelled' | 'pending' | 'added'
    hideOnMobile?: boolean
  }
}
function TileCard({
  layout = 'auto',
  title,
  subtitle,
  icon,
  selected,
  value,
  disabled,
  interactionIcon = 'check',
  onClick,
  withOutBackground = false,
  statusLabel,
}: Props) {
  const handleClick = useCallback(() => {
    onClick(value)
  }, [value, onClick])
  return (
    <Container
      className={clsx(`layout-${layout}`, { selected, withOutBackground })}
      onClick={handleClick}
      disabled={disabled}
    >
      <SymbolContainer>
        {icon}
        {statusLabel && <StatusLabelDesktop variant={statusLabel.variant}>{statusLabel.text}</StatusLabelDesktop>}
      </SymbolContainer>

      <ContentContainer gap={2}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" fullWidth>
          <Heading variant="heading6" align="start" lineClamp={2} wrap="word-wrap"> {title} </Heading>

          {statusLabel && !statusLabel.hideOnMobile && <StatusLabelMobile variant={statusLabel.variant}>{statusLabel.text}</StatusLabelMobile>}
        </Group>
        {!!subtitle && <BodyText variant="medium" align="start" lineClamp={2} colour="neutral-three" wrap="word-wrap"> {subtitle} </BodyText>}
      </ContentContainer>

      {selected && <IconContainer>
        {interactionIcon === 'check' && <SolidCheckCircleIcon size={20} />}
        {interactionIcon === 'right' && <SolidAngleRightIcon size={20} />}
      </IconContainer>}
    </Container>
  )
}

export default TileCard
